'use client';

import { useCallback, useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useAuth } from '@/lib/auth/context';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import AppSidebar from '@/components/AppSidebar';
import MobileTabBar from '@/components/MobileTabBar';
import Topbar from '@/components/shell/Topbar';
import CommandBar from '@/components/shell/CommandBar';
import type { SubscriptionStatus } from '@/types';
import { displayAuthEmail, displayAuthPhone } from '@/lib/demo-privacy';

const SIDEBAR_EXPANDED_PX = 240;
const SIDEBAR_MINI_PX = 56;

type WorkspaceNavState = {
  sectionLabel: string;
  roleLabel: string;
  showAdvancedWorkspaceItems: boolean;
  showPracticeItem: boolean;
  showAddCaseItem: boolean;
  showBulkAddItem: boolean;
  showPlansItem: boolean;
};

const DEFAULT_WORKSPACE_NAV: WorkspaceNavState = {
  sectionLabel: 'Account',
  roleLabel: 'Member',
  showAdvancedWorkspaceItems: false,
  showPracticeItem: false,
  showAddCaseItem: false,
  showBulkAddItem: false,
  showPlansItem: true,
};

function workspaceNavFromStatus(status: SubscriptionStatus | null | undefined): WorkspaceNavState {
  const tier = status?.subscription?.tier;
  const hasActiveTrial = !!status?.isTrial && !status?.isTrialExpired;
  const roleLabel = status?.team?.role === 'owner' ? 'Admin' : 'Member';
  const sectionLabel = tier === 'corporate' ? 'Corporate' : tier === 'firm' ? 'Firm' : 'Account';
  const showAdvancedWorkspaceItems =
    !!status?.isTester || hasActiveTrial || tier === 'lawyer_50' || tier === 'lawyer_pro' || tier === 'firm' || tier === 'corporate';
  const hasPaidCaseWorkspace =
    !!status?.isTester || hasActiveTrial || (!!status?.hasActiveSubscription && !status?.isFreeUser && !status?.isExpired);
  const showAddCaseItem =
    hasPaidCaseWorkspace || !!status?.canAddCase;
  const showBulkAddItem = hasPaidCaseWorkspace;
  const showPlansItem = !hasPaidCaseWorkspace;

  return {
    sectionLabel,
    roleLabel,
    showAdvancedWorkspaceItems,
    showPracticeItem: !!status?.isTester,
    showAddCaseItem,
    showBulkAddItem,
    showPlansItem,
  };
}

function MobileWorkspaceHeader() {
  const { user, signOut } = useAuth();

  const handleLogout = useCallback(async () => {
    await signOut();
    window.location.href = '/';
  }, [signOut]);

  const accountLabel = displayAuthEmail(user?.email) || displayAuthPhone(user) || 'Account';

  return (
    <header className="sticky top-0 z-[65] border-b border-gray-200 bg-white/95 backdrop-blur md:hidden">
      <div className="flex h-14 items-center justify-between gap-2 px-4">
        <Link href="/dashboard" className="flex min-w-0 items-center gap-2.5" aria-label="Go to dashboard">
          <Image src="/logo.png" alt="" width={32} height={32} className="rounded-full" priority />
          <span className="truncate text-base font-semibold text-primary">India Case Status</span>
        </Link>

        <div className="flex shrink-0 items-center gap-1">
          <Link
            href="/add-case"
            className="inline-flex min-h-11 items-center justify-center gap-1.5 rounded-lg bg-primary px-3 text-sm font-semibold text-white shadow-sm hover:bg-primary-700"
            aria-label="Find and track a case"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
              <circle cx="11" cy="11" r="6.5" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M20 20l-3.8-3.8" />
            </svg>
            <span>Find case</span>
          </Link>
          <button
            type="button"
            onClick={handleLogout}
            className="inline-flex h-11 w-11 items-center justify-center rounded-lg text-gray-600 hover:bg-red-50 hover:text-red-600"
            aria-label={`Logout ${accountLabel}`}
            title="Logout"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </button>
        </div>
      </div>
    </header>
  );
}

export default function AppShell({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const pathname = usePathname();

  const [collapsed, setCollapsed] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [isDesktopShell, setIsDesktopShell] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [workspaceNav, setWorkspaceNav] = useState<WorkspaceNavState>(DEFAULT_WORKSPACE_NAV);

  // Read saved preference after mount (avoids SSR/localStorage mismatch).
  useEffect(() => {
    let cancelled = false;
    async function restoreSidebarPreference() {
      await Promise.resolve();
      if (cancelled) return;
      try {
        const saved = localStorage.getItem('ics_sidebar');
        if (saved === 'mini') setCollapsed(true);
      } catch {}
      setMounted(true);
    }

    restoreSidebarPreference();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const media = window.matchMedia('(min-width: 768px)');
    const syncDesktopShell = () => setIsDesktopShell(media.matches);

    syncDesktopShell();

    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', syncDesktopShell);
      return () => media.removeEventListener('change', syncDesktopShell);
    }

    media.addListener(syncDesktopShell);
    return () => media.removeListener(syncDesktopShell);
  }, []);

  useEffect(() => {
    if (!mounted) return;
    try { localStorage.setItem('ics_sidebar', collapsed ? 'mini' : 'full'); } catch {}
  }, [collapsed, mounted]);

  // ⌘K palette — only when authenticated.
  useEffect(() => {
    if (!isAuthenticated) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setCmdOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;

    let cancelled = false;
    async function loadWorkspaceNav() {
      try {
        const res = await fetch('/api/subscription');
        if (!res.ok) return;
        const status = await res.json() as SubscriptionStatus;
        if (!cancelled) setWorkspaceNav(workspaceNavFromStatus(status));
      } catch {
        // Keep conservative account navigation if subscription lookup is unavailable.
      }
    }

    loadWorkspaceNav();
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  const toggleSidebar = useCallback(() => setCollapsed((c) => !c), []);
  const openCmd = useCallback(() => setCmdOpen(true), []);
  const closeCmd = useCallback(() => setCmdOpen(false), []);

  if (pathname.startsWith('/admin')) {
    return (
      <>
        <Navbar />
        <main className="flex-1">{children}</main>
      </>
    );
  }

  if (
    pathname.startsWith('/lp/') ||
    pathname === '/setup' ||
    pathname.startsWith('/setup/') ||
    pathname === '/onboarding/phone' ||
    pathname === '/litigant' ||
    pathname.startsWith('/litigant/') ||
    pathname === '/track-your-case' ||
    pathname === '/track-your-case-us'
  ) {
    return <main className="flex-1">{children}</main>;
  }

  if (isLoading) {
    return <main className="flex-1">{children}</main>;
  }

  if (isAuthenticated) {
    const sidebarWidth = isDesktopShell ? (collapsed ? SIDEBAR_MINI_PX : SIDEBAR_EXPANDED_PX) : 0;
    return (
      <>
        {isDesktopShell && (
          <AppSidebar
            className="hidden md:flex"
            collapsed={collapsed}
            onToggle={toggleSidebar}
            onOpenCmd={openCmd}
            workspaceNav={workspaceNav}
          />
        )}

        <div
          className="ics-shell-main flex-1 flex flex-col min-w-0 overflow-x-hidden pb-20 md:pb-0"
          style={{ ['--ics-sidebar-w' as string]: `${sidebarWidth}px` }}
        >
          {isDesktopShell && (
            <div className="hidden md:block">
              <Topbar onOpenCmd={openCmd} />
            </div>
          )}
          {!isDesktopShell && <MobileWorkspaceHeader />}
          <main className="flex-1 min-w-0">
            {children}
          </main>
        </div>

        <MobileTabBar
          className="md:hidden"
          showAdvancedWorkspaceItems={workspaceNav.showAdvancedWorkspaceItems}
          showPracticeItem={workspaceNav.showPracticeItem}
          showAddCaseItem={workspaceNav.showAddCaseItem}
          showBulkAddItem={workspaceNav.showBulkAddItem}
          showPlansItem={workspaceNav.showPlansItem}
        />

        <CommandBar
          open={cmdOpen}
          onClose={closeCmd}
          showAdvancedWorkspaceItems={workspaceNav.showAdvancedWorkspaceItems}
          showPracticeItem={workspaceNav.showPracticeItem}
          showAddCaseItem={workspaceNav.showAddCaseItem}
          showBulkAddItem={workspaceNav.showBulkAddItem}
          showPlansItem={workspaceNav.showPlansItem}
        />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
    </>
  );
}
